import React, { Dispatch, useEffect, useState } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { Button, InputField, SelectField, SubHeader } from 'vetrf-ui';
import { RemoveModal } from 'components/RemoveModal';
import { exportExcelApi } from 'api/exportExcel';
import warning from 'utils/logger';
import { EntryType, ExcelFields, ExcelTemplate, ProductType } from '../../types/api';
import { ActionKind, ExportAction } from './types';
import { getItemDiff } from './utils';
import styles from './ExportExcelModal.module.scss';

type TemplateBlockProps = {
  dispatch: Dispatch<ExportAction>;
  excelItems: Record<string, ExcelFields[]>;
  defaultFields: ExcelFields[];
  entryType: EntryType;
  productType: ProductType;
};

export const TemplateBlock = ({
  dispatch,
  excelItems,
  defaultFields,
  entryType,
  productType,
}: TemplateBlockProps) => {
  const [templateOptions, setTemplateOptions] = useState<ExcelTemplate[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<{ header: string; defaultValue?: string }>({
    header: '',
  });
  const { setValue, getValues } = useFormContext();
  const templateValue = useWatch({ name: 'template' });
  const templateValueUpdate = getValues('template');

  const handleToggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleOpenForm = (header: string, defaultValue?: string) => {
    setFormData({ header, defaultValue });
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setFormData({ header: '', defaultValue: undefined });
    setValue('templateName', null);
  };

  const getTemplateOptions = async (id?: string) => {
    const res = await exportExcelApi.getTemplate(entryType, productType);
    setTemplateOptions(res);
    const defaultTemplate = id ? res.find((el) => el.id === id) : res.find((el) => el.isDefault);
    if (defaultTemplate) {
      setValue('template', defaultTemplate);
    }
  };

  const handleDelete = () => {
    exportExcelApi
      .deleteTemplate(templateValue.id)
      .then(() => {
        handleToggleModal();
        getTemplateOptions();
      })
      .catch(warning);
  };

  const handleSetDefault = () => {
    exportExcelApi
      .setDefaultTemplate(entryType, productType, templateValue.id)
      .then(() => getTemplateOptions())
      .catch(warning);
  };

  const handleSubmit = () => {
    const formattedValues = {
      entryType,
      productType,
      id: formData.defaultValue ? templateValue.id : undefined,
      name: getValues('templateName'),
      fields: excelItems.selectedItems,
    };
    exportExcelApi
      .saveTemplate(entryType, productType, formattedValues)
      .then((res) => {
        handleCloseForm();
        getTemplateOptions(res.id);
      })
      .catch(warning);
  };

  useEffect(() => {
    getTemplateOptions();
  }, []);

  useEffect(() => {
    if (templateValue) {
      dispatch({
        type: ActionKind.TEMPLATE,
        itemsArray: getItemDiff(defaultFields, templateValue.fields),
        selectedItemsArray: templateValue.fields,
      });
    }
  }, [templateValueUpdate]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-15">
        <div className={styles.templates}>
          <SelectField
            fieldClassName={`selectpicker ${styles.templateInput}`}
            labelClassName="control-label mr-15"
            labelKey="name"
            valueKey="id"
            labelText="Шаблон"
            name="template"
            placeholder="Выберите значение из списка"
            options={templateOptions}
            disabled={isFormOpen}
          />
        </div>
        <div className="ml-auto mt-auto mb-5">
          <div className="d-flex justify-content-end align-items-center gap-5">
            {templateOptions?.length && (
              <>
                {templateValue?.isDefault ? (
                  <Button
                    icon="star"
                    iconColor="orange"
                    iconSize={200}
                    onlyIcon
                    tooltip="Шаблон по умолчанию"
                    disabled
                  />
                ) : (
                  <Button
                    icon="star-half-o"
                    iconColor="orange"
                    iconSize={200}
                    onlyIcon
                    tooltip="Сделать шаблоном по умолчанию"
                    onClick={handleSetDefault}
                  />
                )}
              </>
            )}
            <Button
              icon="plus"
              iconColor="green"
              iconSize={200}
              onlyIcon
              tooltip="Добавить новый шаблон"
              onClick={() => handleOpenForm('Добавление нового шаблона')}
            />
            {templateOptions?.length && (
              <>
                <Button
                  icon="pencil"
                  iconColor="blue"
                  iconSize={200}
                  onlyIcon
                  tooltip="Редактировать шаблон"
                  onClick={() => handleOpenForm('Редактирование шаблона', templateValue?.name)}
                />
                {!templateValue?.isDefault && (
                  <Button
                    icon="trash"
                    iconColor="red"
                    iconSize={200}
                    onlyIcon
                    tooltip="Удалить шаблон"
                    onClick={handleToggleModal}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {isFormOpen && (
        <>
          <SubHeader
            header={formData.header}
            className="d-flex mt-15"
            icon="book"
            iconColor="blue"
            iconSize={100}
            level={5}
          />
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-15">
            <div className={styles.templates}>
              <InputField
                fieldClassName={styles.templateInput}
                labelClassName="control-label mr-15"
                labelText="Название"
                name="templateName"
                required="Необходимо заполнить поле"
                defaultValue={formData.defaultValue}
              />
            </div>
            <div className="ml-auto mt-auto mb-5">
              <div className="d-flex justify-content-end align-center gap-5">
                <Button
                  icon="check"
                  iconColor="green"
                  iconSize={200}
                  onlyIcon
                  tooltip="Сохранить"
                  onClick={handleSubmit}
                />
                <Button
                  icon="undo"
                  iconColor="grey"
                  iconSize={200}
                  onlyIcon
                  tooltip="Отмена"
                  onClick={handleCloseForm}
                />
              </div>
            </div>
          </div>
        </>
      )}
      {isModalOpen && (
        <RemoveModal
          onClose={handleToggleModal}
          onRemove={handleDelete}
          modalContent="Вы уверены, что хотите удалить этот шаблон?"
        />
      )}
    </>
  );
};
